// Admin sign-in through Supabase Auth (email + password).
// Every function returns { error } where error is a plain-English string or null.
// The page check here is only for the screen - the database's is_admin() rules
// are what really protect the data.
import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

export var NOT_CONNECTED =
  'The website can’t reach its database right now (its settings are missing). Please contact whoever set up the site.';

var MIN_PASSWORD = 8;

// Turn a Supabase error into a sentence Andre can act on.
export function friendlyError(err) {
  if (!err) return null;
  var code = (err.code || '').toString();
  var msg = (err.message || String(err)).toLowerCase();
  var status = err.status;

  if (code === 'invalid_credentials' || msg.indexOf('invalid login credentials') !== -1) {
    return 'That email and password don’t match. Check them and try again.';
  }
  if (code === 'email_not_confirmed' || msg.indexOf('email not confirmed') !== -1) {
    return 'This email address hasn’t been confirmed yet. Look for the confirmation email, then try again.';
  }
  if (code === 'user_banned') {
    return 'This account has been switched off. Please contact whoever set up the site.';
  }
  if (code.indexOf('rate_limit') !== -1 || status === 429 || msg.indexOf('rate limit') !== -1 ||
      msg.indexOf('too many') !== -1 || msg.indexOf('security purposes') !== -1) {
    return 'Too many tries in a short time. Please wait a few minutes and try again.';
  }
  if (code === 'same_password' || msg.indexOf('should be different') !== -1) {
    return 'That is the password you already have. Please choose a new one.';
  }
  if (code === 'weak_password' || msg.indexOf('password should') !== -1) {
    return 'That password is too easy to guess. Use at least ' + MIN_PASSWORD + ' characters, mixing letters and numbers.';
  }
  if (code === 'session_not_found' || code === 'session_expired' || code === 'otp_expired' ||
      msg.indexOf('auth session missing') !== -1 || msg.indexOf('expired') !== -1) {
    return 'This link has expired or was already used. Ask for a new reset email and use the newest one.';
  }
  if (msg.indexOf('failed to fetch') !== -1 || msg.indexOf('network') !== -1 || msg.indexOf('load failed') !== -1) {
    return 'Can’t reach the server. Check your internet connection and try again.';
  }
  if (msg.indexOf('email') !== -1 && msg.indexOf('invalid') !== -1) {
    return 'That doesn’t look like a valid email address.';
  }
  return 'Something went wrong. Please try again in a moment.';
}

function looksLikeEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export async function signIn(email, password) {
  if (!supabase) return { error: NOT_CONNECTED };
  var cleanEmail = (email || '').trim();
  if (!cleanEmail || !password) return { error: 'Please enter your email and password.' };
  if (!looksLikeEmail(cleanEmail)) return { error: 'That doesn’t look like a valid email address.' };
  try {
    var res = await supabase.auth.signInWithPassword({ email: cleanEmail, password: password });
    return { error: friendlyError(res.error) };
  } catch (e) {
    return { error: friendlyError(e) };
  }
}

export async function signOut() {
  if (!supabase) return { error: NOT_CONNECTED };
  try {
    var res = await supabase.auth.signOut();
    return { error: friendlyError(res.error) };
  } catch (e) {
    return { error: friendlyError(e) };
  }
}

export async function sendPasswordReset(email) {
  if (!supabase) return { error: NOT_CONNECTED };
  var cleanEmail = (email || '').trim();
  if (!cleanEmail) return { error: 'Please enter your email address first.' };
  if (!looksLikeEmail(cleanEmail)) return { error: 'That doesn’t look like a valid email address.' };
  try {
    var res = await supabase.auth.resetPasswordForEmail(cleanEmail, {
      redirectTo: window.location.origin + '/admin/reset',
    });
    return { error: friendlyError(res.error) };
  } catch (e) {
    return { error: friendlyError(e) };
  }
}

export async function updatePassword(newPassword) {
  if (!supabase) return { error: NOT_CONNECTED };
  if (!newPassword || newPassword.length < MIN_PASSWORD) {
    return { error: 'Please use at least ' + MIN_PASSWORD + ' characters.' };
  }
  try {
    var res = await supabase.auth.updateUser({ password: newPassword });
    return { error: friendlyError(res.error) };
  } catch (e) {
    return { error: friendlyError(e) };
  }
}

// Ask the database whether the signed-in user is in the admins table.
// Any failure counts as "not an admin" - never let someone in by accident.
export async function checkIsAdmin() {
  if (!supabase) return false;
  try {
    var res = await supabase.rpc('is_admin');
    if (res.error) {
      console.warn('is_admin check failed:', res.error.message);
      return false;
    }
    return res.data === true;
  } catch (e) {
    console.warn('is_admin check failed:', e);
    return false;
  }
}

// { session, isAdmin, loading } - updates live when the user signs in or out.
export function useAdminSession() {
  const [session, setSession] = useState(null);
  const [sessionReady, setSessionReady] = useState(!supabase);
  const [adminCheck, setAdminCheck] = useState({ userId: null, isAdmin: false });

  useEffect(function() {
    if (!supabase) return undefined;
    var alive = true;
    supabase.auth.getSession().then(function(res) {
      if (!alive) return;
      setSession((res && res.data && res.data.session) || null);
      setSessionReady(true);
    }).catch(function() {
      if (!alive) return;
      setSession(null);
      setSessionReady(true);
    });
    // Keep this callback synchronous: awaiting other Supabase calls inside it can lock up.
    var sub = supabase.auth.onAuthStateChange(function(_event, newSession) {
      if (!alive) return;
      setSession(newSession || null);
      setSessionReady(true);
      // Forget the old admin answer on sign-out, so the next sign-in is checked afresh.
      if (!newSession) setAdminCheck({ userId: null, isAdmin: false });
    });
    return function() {
      alive = false;
      if (sub && sub.data && sub.data.subscription) sub.data.subscription.unsubscribe();
    };
  }, []);

  var userId = session && session.user ? session.user.id : null;

  useEffect(function() {
    if (!userId) return undefined;
    var alive = true;
    checkIsAdmin().then(function(ok) {
      if (alive) setAdminCheck({ userId: userId, isAdmin: ok });
    });
    return function() { alive = false; };
  }, [userId]);

  var checkedThisUser = adminCheck.userId === userId;
  var loading = !sessionReady || (!!userId && !checkedThisUser);
  return {
    session: session,
    isAdmin: !!userId && checkedThisUser && adminCheck.isAdmin,
    loading: loading,
  };
}
